goog.module('yext.mvc.Collection');
goog.module.declareLegacyNamespace();

const yext = goog.require('yext');
const EventEmitter = goog.require('yext.mvc.EventEmitter');
const Model = goog.require('yext.mvc.Model');


/**
 * @constructor
 * @extends {EventEmitter}
 * @param {function()=} opt_model The constructor function for your model class
 * @param {boolean=} opt_uniqueItemId Should each item in the collection have a unique item id
 */
const Collection = function(opt_model, opt_uniqueItemId) {
  EventEmitter.call(this);

  /**
   * A reference to constructor function that
   * our collection models should be created from.
   * @type {<T>}
   */
  this.model = opt_model || Model;

  /**
   * Indicates whether or not each item id should be unique in the collection
   * @param {boolean}
   */
  this._uniqueItemId = opt_uniqueItemId || false;

  /**
   * @type {!Array.<Model>}
   */
  this._items = [];

  /**
   * Keeps track of things that have changed from the previous state.
   * This gets reset and updated each type .set() is called.
   * @private
   */
  this._changes = {
    'updated': [],
    'added': [],
    'removed': [],
  };
};
goog.inherits(Collection, EventEmitter);

/**
 * NOTE(billy) Any `items` that are native javascript objects (e.g. json),
 * will get `cast` to instances of the `model`.
 * @param {!Object|Array.<Model>} opts Can be the list of items, or an object providing items as a key
 */
Collection.prototype.init = function(opts) {
  var items = [];
  if (Array.isArray(opts)) {
    items = opts;
  } else {
    items = opts.items;
  }

  this.set(items, true);
  return this;
};

Collection.prototype.set = function(models, suppressEvents) {
  if (!Array.isArray(models)) {
    throw new Error('Collection.set(): Can not set models, expected Array.<Model>');
  }

  // Make sure our input is the type of objects that our collection expects
  models = this._convertToInstances(models);

  this._set(models);
  if (this.hasChanges() && !suppressEvents) {
    this.emit('update', this);
  }
};

/**
 * Attempts to do a smart set, by updating the current collection only if necessary.
 * It also keeps track of the type of changes that happend (e.g. added, removed, updated)
 * @param {!Array.<Model>} models List of models to apply to old collection
 * TODO(billy) Sort order probably matters
 */
Collection.prototype._set = function(models) {
  this._clearChanges();

  // Be smart with updating the current collection by:
  // 1. merging models together if they already exist in the collection
  // 2. adding new models that don't exist to the collection
  for (var m = 0; m < models.length; m ++) {
    var model = models[m];
    var collectionIndex = this.findIndex(model);

    // If our model exists in our current collection,
    // compare that model to the existing one, and update if necessary
    if (collectionIndex > -1) {
      var origModel = this.at(collectionIndex);
      var differences = origModel.findDelta(model);

      if (differences.hasDelta) {
        this.at(collectionIndex).set(differences.delta);
        this._trackChange('updated', this.at(collectionIndex));
      }
    } else {
      this.push(model);
      this._trackChange('added', this.at(this.length() - 1));
    }
  }

  // Compare our old list with our new list,
  // and remove any items from our old list that are missing from our new list
  var removableIndexes = this._findRemovableIndices(models);
  for (var r = 0; r < removableIndexes.length; r ++) {
    this._trackChange('removed', this.at(removableIndexes[r]));
    this.removeAt(removableIndexes[r]);
  }
};

Collection.prototype.getItems = function() {
  return this._items;
};

Collection.prototype.containsId = function(id) {
  return this.getById(id) !== null;
};

Collection.prototype.getById = function(id) {
  // TODO(billy) This can be constant time lookup,
  // if we had an up to date map<id,model>
  for (var i = 0; i < this._items.length; i ++) {
    if (this.at(i).get('id') === id || this.at(i).id === id) {
      return this.at(i);
    }
  }

  return null;
};

/**
 * convertToInstances is a helper to transform javascript objects into
 * instances of `this.model` class.
 * @param {!Array.<Model>} objs Objects to convert to instances
 */
Collection.prototype._convertToInstances = function(objs) {
  var instances = [];
  if (!objs) {
    return instances;
  }

  for (var m = 0; m < objs.length; m ++) {
    var obj = objs[m];

    // If our input `obj` is already instance of our expected model class,
    // then we don't need to do anymore work!
    if (obj.constructor === this.model || obj.constructor.prototype instanceof this.model) {
      instances.push(obj);
      continue;
    }

    // Otherwise, we try to convert a `native object` to an instance of our class.
    // If our object is not native, we have no idea what to do.
    if (obj.constructor !== window.Object) {
      throw new Error('Collection._convertToInstances(): unable to set models, invalid type provided.');
    }

    obj = new this.model().init(obj);
    instances.push(obj);
  }

  return instances;
};

/**
 * convertToInstance is a helper to transform a native javascript object into
 * an instance of `this.model` class.
 * @param {Model} obj
 */
Collection.prototype._convertToInstance = function(obj) {
  return this._convertToInstances([obj])[0];
};

Collection.prototype.findIndex = function(model) {
  // TODO(billy) Perhaps allow the interface to take in a model or id
  if (model === undefined) {
    return -1;
  }
  var len = this._items.length;
  for (var i = 0; i < len; i ++) {
    var other = this._items[i];
    if (model.equals(other)
        && (this._uniqueItemId || !model.findDelta(other.raw()).hasDelta)) {
      return i;
    }
  }

  return -1;
};

/**
 * findRemovableIndices will compare a new list of models to the
 * current list of models. It will return a list of models that
 * the current list has but the new list doesn't.
 * @param {!Array.<Model>} models The new models to compare against the current
 * @return {!Array.<number>}
 */
Collection.prototype._findRemovableIndices = function(models) {
  var removableIndexes = [];

  // TODO(billy) If we kept items sorted by id when they get added,
  // we could make this a much faster operation (e.g. log(n) instead of n*n).
  // But, since our collections will generally be relatively small (e.g. < 25 items)
  // I decided that being lazy is fine for now.
  for (var i = 0; i < this._items.length; i ++) {
    var found = false;
    for (var m = 0; m < models.length; m ++) {
      if (models[m].get('id') === this.at(i).get('id')) {
        found = true;
        continue;
      }
    }

    if (!found) {
      removableIndexes.push(i);
    }
  }

  return removableIndexes;
};

/**
 * hasChanges determines if anything was changed from the previous 'set'
 * @return {boolean} true if something changed
 */
Collection.prototype.hasChanges = function() {
  return this._changes['updated'].length > 0
         || this._changes['added'].length > 0
         || this._changes['removed'].length > 0;
};

Collection.prototype.getChanges = function(type) {
  if (!type) {
    return this._changes;
  }

  return this._changes[type];
};

Collection.prototype._clearChanges = function() {
  this._changes = {
    'updated': [],
    'added': [],
    'removed': [],
  };
};

/**
 * records a change, given a type and model
 * @param {string} type
 */
Collection.prototype._trackChange = function(type, model) {
  this._changes[type].push(model);
};

/**
 * bindToModel will forward events from a model to the collection.
 * providing the model that was changed as an argument
 */
Collection.prototype._bindToModel = function(eventNames, model) {
  var collection = this;
  model.on(eventNames, function(evt, m) {
    this._clearChanges();
    this._trackChange('updated', model);

    collection.emit(evt.type, m, collection);
    // Broadcast a change for each property that was changed on the model
    // This supports use cases: m.on('change:property', function() { ... });
    for (var changedProp in m.getChanges()) {
      collection.emit(evt.type + ':' + changedProp, model, collection);
    }
  }.bind(this));
};

Collection.prototype.each = function(fn) {
  var len = this._items.length;
  for (var i = 0; i < len; i ++) {
    fn(this.at(i));
  }
};

Collection.prototype.empty = function() {
  var len = this._items.length - 1;
  for (var i = len; i > -1; i --) {
    this.removeAt(i);
  }
};

Collection.prototype.resetState = function(items) {
  this.empty();
  this.set(items);
  this.emit('reset', this);
};

Collection.prototype.remove = function(model) {
  this.removeAt(this.findIndex(model));
  return this;
};

Collection.prototype.removeAt = function(index) {
  var model = this.at(index);
  if (model) {
    // TODO simply call model.off() when supported
    model.off('change,reset,update,add,remove');
    this._items.splice(index, 1);

    this._clearChanges();
    this._trackChange('removed', model);

    this.emit('update', this, model);
    this.emit('remove', model, this);
  }
  return this;
};

Collection.prototype.add = function(model) {
  // TODO(slanden): There's a weird bug where triggering the 'add' event causes this method
  // to execute. This is a temporary fix but that should definitely be addressed
  if (model === undefined) {
    return this;
  }
  this._clearChanges();
  return this.push(model);
};

Collection.prototype.push = function(model) {
  model = this._convertToInstance(model);

  if (this._uniqueItemId && this.findIndex(model) > -1) {
    // TODO(billy) This should probably update the existing model in the collection?
    return;
  }

  // Events on the model will propagate to the collection
  // providing the model as the first argument
  this._bindToModel('change,reset', model);

  this._items.push(model);

  this._trackChange('added', model);
  this.emit('update', this, model);
  this.emit('add', model, this);

  return this;
};

Collection.prototype.pop = function() {
  var model = this.at(this.length() - 1);
  this.removeAt(this.length() - 1);
  return model;
};

Collection.prototype.at = function(index) {
  return this._items[index];
};

/** @return {number} */
Collection.prototype.length = function() {
  return this._items.length;
};

Collection.prototype.raw = function() {
  var raw = [];
  this.each(function(model) {
    raw.push(model.raw());
  });
  return raw;
};

Collection.prototype.asJSON = function() {
  return yext.JSON.stringify(this.raw());
};

/**
 * Gets all of the models in the collection which have the given attribute set to the given value
 *
 * @param {string} attribute
 * @param {?} value
 * @return {!Array.<Model>}
 */
Collection.prototype.getByAttribute = function(attribute, value) {
  var results = [];
  for (var i = 0; i < this._items.length; i ++) {
    if (this.at(i).get(attribute) === value) {
      results.push(this.at(i));
    }
  }
  return results;
};

Collection.prototype.filter = function(fn) {
  return this._items.filter(fn);
};

Collection.prototype.reduce = function(callback, initalValue) {
  return this._items.reduce(callback, initalValue);
};

Collection.prototype.map = function(fn) {
  return this._items.map(fn);
};

Collection.prototype.sort = function(fn) {
  return this._items.sort(fn);
};

exports = Collection;
